import { useEffect, useRef } from 'react';

// Drives a --reveal-progress (0-1) CSS custom property directly off scroll
// position, recomputed every frame while scrolling - not a fixed-duration
// transition triggered once. CSS reads the variable to fade/scale content,
// so motion tracks scroll speed exactly instead of playing out on a timer.
// Progress is never frozen, so scrolling back up through the reveal band
// fades the row back out - it's reversible, not a one-time reveal.
const registry = new Set<HTMLElement>();
let ticking = false;

function computeProgress(el: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const start = vh * 0.98; // begins right as the row's top edge enters the viewport
  const end = vh * 0.25; // finishes only once it's scrolled most of the way up - a longer run
  const raw = (start - rect.top) / (start - end);
  return Math.min(1, Math.max(0, raw));
}

// Pure function of scroll position (not time), so it stays perfectly
// reversible on scroll-up: overshoots past 1 near the end of the run before
// settling exactly at 1, giving a "pop" instead of a flat linear fade.
function easeOutBack(x: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

function tick() {
  ticking = false;
  registry.forEach((el) => {
    const progress = computeProgress(el);
    el.style.setProperty('--reveal-progress', progress.toFixed(3));
    el.style.setProperty('--reveal-pop', easeOutBack(progress).toFixed(3));
  });
}

function onScroll() {
  if (!ticking) {
    ticking = true;
    requestAnimationFrame(tick);
  }
}

export function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.style.setProperty('--reveal-progress', '1');
      el.style.setProperty('--reveal-pop', '1');
      return;
    }

    el.style.setProperty('--reveal-progress', computeProgress(el).toFixed(3));
    el.style.setProperty('--reveal-pop', easeOutBack(computeProgress(el)).toFixed(3));
    registry.add(el);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      registry.delete(el);
      if (registry.size === 0) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    };
  }, []);

  return ref;
}
